import {
  Directive,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  booleanAttribute,
} from '@angular/core';
import type { DsContentSheetShadow } from '../containment/ds-content-sheet.component';

/** Stable application-defined value of an item that can open a Header panel. */
export type DsHeaderPanelAction = string;

/** Integration values supplied by Header to a projected panel. */
export interface DsHeaderPanelContext {
  action: DsHeaderPanelAction;
  open: boolean;
  anchor: HTMLElement | null;
  overlayBoundary: HTMLElement | null;
  close: () => void;
}

/** Angular template context for `ng-template[dsHeaderPanel]`. */
export interface DsHeaderPanelTemplateContext extends DsHeaderPanelContext {
  $implicit: DsHeaderPanelContext;
}

/** Legacy context retained for the body/footer projection API. */
export interface DsHeaderLegacyPanelTemplateContext {
  close: () => void;
}

/** Values exposed by the Header to the projected mobile navigation template. */
export interface DsHeaderMobileMenuTemplateContext {
  open: boolean;
  close: () => void;
}

/**
 * Projects a complete Content Sheet for a Header action.
 *
 * The application owns every Content Sheet input and its content. Header only
 * supplies the values required to connect it to the triggering action.
 */
@Directive({
  selector: 'ng-template[dsHeaderPanel]',
  standalone: true,
})
export class DsHeaderPanelDirective {
  @Input({ required: true }) dsHeaderPanel!: DsHeaderPanelAction;

  constructor(readonly templateRef: TemplateRef<DsHeaderPanelTemplateContext>) {}

  static ngTemplateContextGuard(
    _directive: DsHeaderPanelDirective,
    context: unknown,
  ): context is DsHeaderPanelTemplateContext {
    return true;
  }
}

/** Values supplied by Header to content it renders inside its managed Content Sheet. */
export interface DsHeaderManagedPanelContext {
  action: DsHeaderPanelAction;
  close: () => void;
}

/** Angular template context shared by the managed header, body and footer slots. */
export interface DsHeaderManagedPanelTemplateContext extends DsHeaderManagedPanelContext {
  $implicit: DsHeaderManagedPanelContext;
}

/**
 * Projects only the body of a Header panel and optionally configures the
 * Content Sheet that Header creates and controls for that action.
 */
@Directive({
  selector: 'ng-template[dsHeaderPanelContent]',
  standalone: true,
})
export class DsHeaderPanelContentDirective {
  @Input({ required: true }) dsHeaderPanelContent!: DsHeaderPanelAction;
  @Input() title?: string;
  @Input() descriptiveText?: string;
  @Input() ariaLabel?: string;
  @Input() maxWidth?: number | string;
  @Input({ transform: booleanAttribute }) dismissOnBackdropClick = true;
  @Input({ transform: booleanAttribute }) showCloseButton = true;
  @Input({ transform: booleanAttribute }) showBackButton = false;
  @Input({ transform: booleanAttribute }) truncateTitle = false;
  @Input() shadow: DsContentSheetShadow = 'l2';
  @Input() closeAriaLabel = 'Cerrar';
  @Input() backAriaLabel = 'Volver';

  @Output() backed = new EventEmitter<void>();

  constructor(readonly templateRef: TemplateRef<DsHeaderManagedPanelTemplateContext>) {}

  static ngTemplateContextGuard(
    _directive: DsHeaderPanelContentDirective,
    context: unknown,
  ): context is DsHeaderManagedPanelTemplateContext {
    return true;
  }
}

/** Projects optional navigation, such as Tabs, below the Content Sheet title. */
@Directive({
  selector: 'ng-template[dsHeaderPanelHeader]',
  standalone: true,
})
export class DsHeaderPanelHeaderDirective {
  @Input({ required: true }) dsHeaderPanelHeader!: DsHeaderPanelAction;

  constructor(readonly templateRef: TemplateRef<DsHeaderManagedPanelTemplateContext>) {}

  static ngTemplateContextGuard(
    _directive: DsHeaderPanelHeaderDirective,
    context: unknown,
  ): context is DsHeaderManagedPanelTemplateContext {
    return true;
  }
}

/** Projects optional actions into the footer of Header's managed Content Sheet. */
@Directive({
  selector: 'ng-template[dsHeaderPanelFooter]',
  standalone: true,
})
export class DsHeaderPanelFooterDirective {
  @Input({ required: true }) dsHeaderPanelFooter!: DsHeaderPanelAction;

  constructor(readonly templateRef: TemplateRef<DsHeaderManagedPanelTemplateContext>) {}

  static ngTemplateContextGuard(
    _directive: DsHeaderPanelFooterDirective,
    context: unknown,
  ): context is DsHeaderManagedPanelTemplateContext {
    return true;
  }
}

/** @deprecated Use `ng-template[dsHeaderPanelContent]`. */
@Directive({
  selector: 'ng-template[dsHeaderContactContent]',
  standalone: true,
})
export class DsHeaderContactContentDirective {
  constructor(readonly templateRef: TemplateRef<DsHeaderLegacyPanelTemplateContext>) {}

  static ngTemplateContextGuard(
    _directive: DsHeaderContactContentDirective,
    context: unknown,
  ): context is DsHeaderLegacyPanelTemplateContext {
    return true;
  }
}

/** @deprecated Use `ng-template[dsHeaderPanelFooter]`. */
@Directive({
  selector: 'ng-template[dsHeaderContactFooter]',
  standalone: true,
})
export class DsHeaderContactFooterDirective {
  constructor(readonly templateRef: TemplateRef<DsHeaderLegacyPanelTemplateContext>) {}

  static ngTemplateContextGuard(
    _directive: DsHeaderContactFooterDirective,
    context: unknown,
  ): context is DsHeaderLegacyPanelTemplateContext {
    return true;
  }
}

/** @deprecated Use `ng-template[dsHeaderPanelContent]`. */
@Directive({
  selector: 'ng-template[dsHeaderProfileContent]',
  standalone: true,
})
export class DsHeaderProfileContentDirective {
  constructor(readonly templateRef: TemplateRef<DsHeaderLegacyPanelTemplateContext>) {}

  static ngTemplateContextGuard(
    _directive: DsHeaderProfileContentDirective,
    context: unknown,
  ): context is DsHeaderLegacyPanelTemplateContext {
    return true;
  }
}

/** @deprecated Use `ng-template[dsHeaderPanelFooter]`. */
@Directive({
  selector: 'ng-template[dsHeaderProfileFooter]',
  standalone: true,
})
export class DsHeaderProfileFooterDirective {
  constructor(readonly templateRef: TemplateRef<DsHeaderLegacyPanelTemplateContext>) {}

  static ngTemplateContextGuard(
    _directive: DsHeaderProfileFooterDirective,
    context: unknown,
  ): context is DsHeaderLegacyPanelTemplateContext {
    return true;
  }
}

@Directive({
  selector: 'ng-template[dsHeaderMobileMenu]',
  standalone: true,
})
export class DsHeaderMobileMenuDirective {
  constructor(readonly templateRef: TemplateRef<DsHeaderMobileMenuTemplateContext>) {}

  static ngTemplateContextGuard(
    _directive: DsHeaderMobileMenuDirective,
    context: unknown,
  ): context is DsHeaderMobileMenuTemplateContext {
    return true;
  }
}
